import React from 'react';
import { Box, Skeleton, Tooltip } from '@mui/material';
import { OpenInNew as OpenIcon, Streetview as StreetViewIcon } from '@mui/icons-material';
import { INTEL_COLORS, INTEL_MONO } from './IntelPopup';
import {
  streetViewDeepLink,
  useStreetViewImage,
  type StreetViewImageStatus,
} from '../../services/cameraStreetViewService';

export type StreetViewThumbProps = {
  lat: number;
  lon: number;
  heading?: number | null;
  /** Fetch only when true (admin switch + embed + key). */
  enabled: boolean;
  width?: number;
  height?: number;
  /** Tap target: open the deep link (default) or run a callback. */
  onClick?: () => void;
  /** Hide the little heading badge. */
  hideBadge?: boolean;
  /** Collapse entirely (render null) when Google has no imagery. */
  collapseWhenMissing?: boolean;
  sx?: Record<string, unknown>;
};

const STATUS_TEXT: Record<StreetViewImageStatus, string> = {
  idle: '',
  loading: 'Loading Street View…',
  ready: '',
  none: 'No Street View imagery here',
  error: 'Preview unavailable',
};

/**
 * Compact Street View still aimed along a camera's heading, pulled through the
 * backend proxy. Degrades to a muted "no imagery" bar, and to nothing when
 * `enabled` is false.
 */
const StreetViewThumb: React.FC<StreetViewThumbProps> = ({
  lat,
  lon,
  heading,
  enabled,
  width = 288,
  height = 150,
  onClick,
  hideBadge = false,
  collapseWhenMissing = false,
  sx,
}) => {
  // Request 2x for crisp rendering on HiDPI, capped at Google's 640 limit.
  const reqW = Math.min(640, width * 2);
  const reqH = Math.min(640, height * 2);
  const { url, status, error } = useStreetViewImage(lat, lon, enabled, {
    heading,
    width: reqW,
    height: reqH,
  });

  if (!enabled) return null;
  if (collapseWhenMissing && (status === 'none' || status === 'error')) return null;

  const href = streetViewDeepLink(lat, lon, heading);
  const headingText = heading == null ? 'Facing unknown' : `${Math.round(heading)}°`;

  const frame = (
    <Box
      component={onClick ? 'div' : 'a'}
      href={onClick ? undefined : href}
      target={onClick ? undefined : '_blank'}
      rel={onClick ? undefined : 'noopener noreferrer'}
      onClick={onClick}
      // Links and images are natively draggable; a native drag cancels the
      // pointer capture of any parent (callouts, insets) that moves by dragging.
      draggable={false}
      onDragStart={(e: React.DragEvent) => e.preventDefault()}
      sx={{
        position: 'relative',
        display: 'block',
        width: '100%',
        aspectRatio: `${width} / ${height}`,
        borderRadius: '6px',
        overflow: 'hidden',
        bgcolor: INTEL_COLORS.bgRaised,
        border: `1px solid ${INTEL_COLORS.border}`,
        cursor: status === 'ready' ? 'pointer' : 'default',
        textDecoration: 'none',
        userSelect: 'none',
        WebkitUserDrag: 'none',
        '&:hover .sv-open': { opacity: 1 },
        ...sx,
      }}
      aria-label={status === 'ready' ? 'Open Street View' : STATUS_TEXT[status]}
    >
      {status === 'loading' && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.06)', height: '100%' }}
        />
      )}
      {status === 'ready' && url && (
        <Box
          component="img"
          src={url}
          alt={`Street View facing ${headingText}`}
          draggable={false}
          onDragStart={(e: React.DragEvent) => e.preventDefault()}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserDrag: 'none',
          }}
        />
      )}
      {(status === 'none' || status === 'error') && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            color: INTEL_COLORS.textDim,
            fontSize: '0.68rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            px: 1,
            textAlign: 'center',
          }}
          // The raw server message ("Request failed with status code 502") is
          // noise for operators; keep it reachable on hover for troubleshooting.
          title={status === 'error' && error ? error : undefined}
        >
          <StreetViewIcon sx={{ fontSize: 16, opacity: 0.6 }} />
          {STATUS_TEXT[status]}
        </Box>
      )}
      {status === 'ready' && !hideBadge && (
        <Box
          sx={{
            position: 'absolute',
            left: 6,
            bottom: 6,
            px: 0.6,
            py: '1px',
            borderRadius: '4px',
            bgcolor: 'rgba(0,0,0,0.6)',
            color: INTEL_COLORS.text,
            fontFamily: INTEL_MONO,
            fontSize: '0.62rem',
            lineHeight: 1.6,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
          }}
        >
          <StreetViewIcon sx={{ fontSize: 11 }} />
          {headingText}
        </Box>
      )}
      {status === 'ready' && (
        <Box
          className="sv-open"
          sx={{
            position: 'absolute',
            right: 6,
            top: 6,
            p: 0.4,
            borderRadius: '4px',
            bgcolor: 'rgba(0,0,0,0.6)',
            color: INTEL_COLORS.text,
            opacity: 0,
            transition: 'opacity 120ms',
            display: 'inline-flex',
          }}
        >
          <OpenIcon sx={{ fontSize: 12 }} />
        </Box>
      )}
    </Box>
  );

  return status === 'ready' && !onClick ? (
    <Tooltip title="Open in Google Street View" placement="top" enterDelay={400}>
      {frame}
    </Tooltip>
  ) : (
    frame
  );
};

export default StreetViewThumb;
